import { useState, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { BsFiletypeJson } from "react-icons/bs";
import { useDropzone } from "react-dropzone";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { addAssessment } from "../../../axios/admin";
import { AdminCreds } from "../../../types";
import { getAssessmentApiErrorMessage } from "./assessment-api-error";
import {
  CONTEST_PRESETS,
  DEFAULT_ANSWERS_TIME_MINUTES,
  DEFAULT_PRESET_ID,
  type ContestPresetId,
} from "./contest-presets";
import {
  applyContestSettingsToJson,
  defaultContestStartTime,
  formatJson,
  hydrateContestSettings,
  tryParseContestJson,
  type ContestFormSettings,
  type ContestSendEmailChoice,
} from "./contest-json";
import {
  CONTEST_TIMEZONES,
  DEFAULT_TIMEZONE_ID,
  convertWallTimeBetweenZones,
  type ContestTimezoneId,
} from "./contest-timezone";
import {
  buttonsContainerStyles,
  dayStyles,
  dropzoneEmptyStyles,
  dropzoneStyles,
  errorTypographyStyles,
  fieldContainerStyles,
  fieldsGridStyles,
  fileNameStyles,
  getInputLabelStyles,
  getSelectStyles,
  getTextFieldStyles,
  headerTypographyStyles,
  jsonTextFieldStyles,
  layoutStyles,
  mainButtonStyles,
  menuItemStyles,
  modalBoxStyles,
  popperStyles,
  presetMenuPaperStyles,
  removeFileButtonStyles,
  secondaryButtonStyles,
  successTypographyStyles,
  yearButtonStyles,
} from "./styles";

interface UploadAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  adminCreds: AdminCreds;
}

export const UploadAssessmentModal = ({
  open,
  onClose,
  adminCreds,
}: UploadAssessmentModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState("");
  const [contestId, setContestId] = useState("");
  const [title, setTitle] = useState("");
  const [answersTimeMinutes, setAnswersTimeMinutes] = useState(
    String(DEFAULT_ANSWERS_TIME_MINUTES)
  );
  const [activeTimeMinutes, setActiveTimeMinutes] = useState(
    String(DEFAULT_ANSWERS_TIME_MINUTES)
  );
  const [startTime, setStartTime] = useState<Dayjs | null>(
    defaultContestStartTime()
  );
  const [timezoneId, setTimezoneId] =
    useState<ContestTimezoneId>(DEFAULT_TIMEZONE_ID);
  const [presetId, setPresetId] =
    useState<ContestPresetId>(DEFAULT_PRESET_ID);
  const [sendEmail, setSendEmail] = useState<ContestSendEmailChoice>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resetContestSettings = () => {
    setContestId("");
    setTitle("");
    setAnswersTimeMinutes(String(DEFAULT_ANSWERS_TIME_MINUTES));
    setActiveTimeMinutes(String(DEFAULT_ANSWERS_TIME_MINUTES));
    setStartTime(defaultContestStartTime());
    setTimezoneId(DEFAULT_TIMEZONE_ID);
    setPresetId(DEFAULT_PRESET_ID);
    setSendEmail(null);
  };

  const applyHydratedSettings = (
    assessment: Record<string, unknown>,
    nextTimezoneId: ContestTimezoneId = timezoneId
  ) => {
    const hydrated = hydrateContestSettings(assessment, nextTimezoneId);
    setContestId(hydrated.contestId);
    setTitle(hydrated.title);
    setAnswersTimeMinutes(hydrated.answersTimeMinutes);
    setActiveTimeMinutes(hydrated.activeTimeMinutes);
    setStartTime(hydrated.startTime);
    setTimezoneId(hydrated.timezoneId);
    setPresetId(hydrated.presetId);
    setSendEmail(hydrated.sendEmail);
  };

  const syncFiltersIntoJson = (settings: ContestFormSettings) => {
    setJsonText((prev) => {
      const parsed = tryParseContestJson(prev);
      if (!parsed) return prev;

      const updated = applyContestSettingsToJson(parsed, settings);
      if (!updated) return prev;

      return JSON.stringify(updated, null, 2);
    });
  };

  const currentSettings = (
    overrides: Partial<ContestFormSettings> = {}
  ): ContestFormSettings => ({
    contestId,
    title,
    answersTimeMinutes,
    activeTimeMinutes,
    startTime,
    timezoneId,
    presetId,
    sendEmail,
    ...overrides,
  });

  const handleTimezoneChange = (nextTimezoneId: ContestTimezoneId) => {
    const nextStartTime =
      startTime && startTime.isValid()
        ? convertWallTimeBetweenZones(startTime, timezoneId, nextTimezoneId)
        : startTime;
    setTimezoneId(nextTimezoneId);
    setStartTime(nextStartTime);
    setFormError(null);
    setSuccessMessage(null);
    syncFiltersIntoJson(
      currentSettings({
        timezoneId: nextTimezoneId,
        startTime: nextStartTime,
      })
    );
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = () => {
      const content =
        typeof reader.result === "string" ? reader.result : "";
      const formatted = formatJson(content);
      setFile(selectedFile);
      setJsonText(formatted);
      setFormError(null);
      setSuccessMessage(null);

      const parsed = tryParseContestJson(formatted);
      if (parsed) applyHydratedSettings(parsed, timezoneId);
    };
    reader.onerror = () => {
      setFormError("Failed to read the selected file");
    };
    reader.readAsText(selectedFile);
  }, [timezoneId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/json": [".json"],
      "text/json": [".json"],
    },
    multiple: false,
  });

  const handleRemoveFile = () => {
    setFile(null);
    setJsonText("");
    setFormError(null);
    setSuccessMessage(null);
    resetContestSettings();
  };

  const handleClose = () => {
    setFile(null);
    setJsonText("");
    setFormError(null);
    setSuccessMessage(null);
    setLoading(false);
    setIsDatePickerOpen(false);
    resetContestSettings();
    onClose();
  };

  const handleJsonChange = (value: string) => {
    setJsonText(value);
    setFormError(null);
    setSuccessMessage(null);
    if (file) setFile(null);
  };

  const handleJsonBlur = () => {
    const parsed = tryParseContestJson(jsonText);
    if (!parsed) return;
    applyHydratedSettings(parsed);
    setJsonText(JSON.stringify(parsed, null, 2));
  };

  const handleSend = async () => {
    const trimmed = jsonText.trim();
    if (!trimmed) {
      setFormError("Paste or upload contest JSON first");
      return;
    }

    const parsed = tryParseContestJson(trimmed);
    if (!parsed) {
      setFormError("Invalid JSON. Please fix the syntax and try again");
      return;
    }

    if (!contestId.trim()) {
      setFormError("Contest id is required");
      return;
    }

    if (sendEmail === null) {
      setFormError("Choose whether to send invitation emails");
      return;
    }

    const payload = applyContestSettingsToJson(parsed, currentSettings());
    if (!payload) {
      if (
        !Number.isInteger(Number(answersTimeMinutes)) ||
        Number(answersTimeMinutes) <= 0
      ) {
        setFormError("Answers time must be a positive number of minutes");
        return;
      }
      if (
        !Number.isInteger(Number(activeTimeMinutes)) ||
        Number(activeTimeMinutes) <= 0
      ) {
        setFormError("Active time must be a positive number of minutes");
        return;
      }
      setFormError("Choose a valid contest start time");
      return;
    }

    setLoading(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      const res = await addAssessment(payload, adminCreds);
      const message =
        typeof res === "string" && res.trim()
          ? res
          : "Contest added successfully";
      setSuccessMessage(message);
      setFile(null);
      setJsonText("");
      resetContestSettings();
    } catch (error) {
      const errorMessage = getAssessmentApiErrorMessage(
        error,
        "Oops... Unknown error happened while uploading contest"
      );
      console.error("Error uploading contest:", errorMessage);
      setFormError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBoxStyles}>
        <Box sx={fieldContainerStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={headerTypographyStyles}
          >
            Upload contest JSON
          </CustomTypography>

          {formError && (
            <Typography variant="body2" sx={errorTypographyStyles}>
              {formError}
            </Typography>
          )}

          {successMessage && (
            <Typography variant="body2" sx={successTypographyStyles}>
              {successMessage}
            </Typography>
          )}
        </Box>

        <form className="flex flex-col gap-[16px]">
          <Box {...getRootProps()} sx={dropzoneStyles(isDragActive)}>
            <input {...getInputProps()} />
            {!file ? (
              <Box sx={dropzoneEmptyStyles}>
                <UploadFileIcon fontSize="large" />
                <CustomTypography variant="body1">
                  Drag & drop a JSON file here, or click to select
                </CustomTypography>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <CustomTypography fontSize={12} sx={fileNameStyles}>
                  <BsFiletypeJson
                    style={{
                      transform: "scale(1.5, 1.5)",
                    }}
                  />
                  <span>{file.name}</span>
                </CustomTypography>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemoveFile();
                  }}
                  size="small"
                  sx={removeFileButtonStyles}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={fieldsGridStyles}>
            <TextField
              fullWidth
              label="Contest id"
              value={contestId}
              onChange={(event) => {
                const nextValue = event.target.value;
                setContestId(nextValue);
                setFormError(null);
                setSuccessMessage(null);
                syncFiltersIntoJson(currentSettings({ contestId: nextValue }));
              }}
              sx={getTextFieldStyles(false)}
            />

            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(event) => {
                const nextValue = event.target.value;
                setTitle(nextValue);
                setFormError(null);
                setSuccessMessage(null);
                syncFiltersIntoJson(currentSettings({ title: nextValue }));
              }}
              sx={getTextFieldStyles(false)}
            />

            <TextField
              fullWidth
              type="number"
              label="Answers time (minutes)"
              value={answersTimeMinutes}
              onChange={(event) => {
                const nextValue = event.target.value;
                setAnswersTimeMinutes(nextValue);
                setFormError(null);
                setSuccessMessage(null);
                syncFiltersIntoJson(
                  currentSettings({ answersTimeMinutes: nextValue })
                );
              }}
              inputProps={{ min: 1, step: 1 }}
              sx={getTextFieldStyles(false)}
            />

            <TextField
              fullWidth
              type="number"
              label="Active time (minutes)"
              value={activeTimeMinutes}
              onChange={(event) => {
                const nextValue = event.target.value;
                setActiveTimeMinutes(nextValue);
                setFormError(null);
                setSuccessMessage(null);
                syncFiltersIntoJson(
                  currentSettings({ activeTimeMinutes: nextValue })
                );
              }}
              inputProps={{ min: 1, step: 1 }}
              sx={getTextFieldStyles(false)}
            />

            <FormControl fullWidth>
              <InputLabel sx={getInputLabelStyles(false)}>
                Start time timezone
              </InputLabel>
              <Select
                label="Start time timezone"
                value={timezoneId}
                onChange={(event) => {
                  handleTimezoneChange(
                    event.target.value as ContestTimezoneId
                  );
                }}
                sx={getSelectStyles(false)}
                MenuProps={{
                  PaperProps: {
                    sx: presetMenuPaperStyles,
                  },
                }}
              >
                {CONTEST_TIMEZONES.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id} sx={menuItemStyles}>
                    {zone.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Contest start time"
                value={startTime}
                onChange={(newValue) => {
                  setStartTime(newValue);
                  setFormError(null);
                  setSuccessMessage(null);
                  syncFiltersIntoJson(
                    currentSettings({ startTime: newValue })
                  );
                }}
                format="DD.MM.YYYY HH:mm"
                ampm={false}
                timeSteps={{ minutes: 5 }}
                disablePast
                open={isDatePickerOpen}
                onOpen={() => {
                  setIsDatePickerOpen(true);
                  setFormError(null);
                }}
                onClose={() => setIsDatePickerOpen(false)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    onClick: () => setIsDatePickerOpen(true),
                    sx: getTextFieldStyles(false),
                  },
                  popper: {
                    sx: popperStyles,
                  },
                  layout: {
                    sx: layoutStyles,
                  },
                  day: {
                    sx: dayStyles,
                  },
                  yearButton: {
                    sx: yearButtonStyles,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <FormControl fullWidth>
            <InputLabel sx={getInputLabelStyles(false)}>Preset</InputLabel>
            <Select
              label="Preset"
              value={presetId}
              onChange={(event) => {
                const nextPresetId = event.target.value as ContestPresetId;
                setPresetId(nextPresetId);
                setFormError(null);
                setSuccessMessage(null);
                syncFiltersIntoJson(
                  currentSettings({ presetId: nextPresetId })
                );
              }}
              sx={getSelectStyles(false)}
              MenuProps={{
                PaperProps: {
                  sx: presetMenuPaperStyles,
                },
              }}
            >
              {CONTEST_PRESETS.map((preset) => (
                <MenuItem key={preset.id} value={preset.id} sx={menuItemStyles}>
                  {preset.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel sx={getInputLabelStyles(false)}>
              Send invitation emails?
            </InputLabel>
            <Select
              label="Send invitation emails?"
              value={sendEmail === null ? "" : sendEmail ? "yes" : "no"}
              onChange={(event) => {
                const nextValue = event.target.value;
                const nextSendEmail: ContestSendEmailChoice =
                  nextValue === "yes"
                    ? true
                    : nextValue === "no"
                      ? false
                      : null;
                setSendEmail(nextSendEmail);
                setFormError(null);
                setSuccessMessage(null);
                if (nextSendEmail !== null) {
                  syncFiltersIntoJson(
                    currentSettings({ sendEmail: nextSendEmail })
                  );
                }
              }}
              sx={getSelectStyles(false)}
              MenuProps={{
                PaperProps: {
                  sx: presetMenuPaperStyles,
                },
              }}
            >
              <MenuItem value="yes" sx={menuItemStyles}>
                Yes
              </MenuItem>
              <MenuItem value="no" sx={menuItemStyles}>
                No
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            multiline
            minRows={12}
            maxRows={24}
            fullWidth
            value={jsonText}
            onChange={(event) => handleJsonChange(event.target.value)}
            onBlur={handleJsonBlur}
            placeholder='{\n  "id": "...",\n  "domain": "Novartis",\n  "focus": "...",\n  "questions": []\n}'
            sx={jsonTextFieldStyles}
          />

          <Box sx={buttonsContainerStyles}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={secondaryButtonStyles}
              disabled={loading}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              sx={mainButtonStyles}
              disabled={loading}
            >
              {loading ? "Uploading..." : "Upload"}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
